import useSWR from "swr";
import { supabase } from "@/integrations/supabase/client";
import { competitions as fallback, getStatus, type Competition, type CompetitionStatus } from "@/data/competitions";

export type CompetitionWithStatus = Competition & { status: CompetitionStatus };

async function fetchFromSupabase(): Promise<Competition[]> {
  const { data, error } = await supabase
    .from("wsl_events")
    .select("*")
    .order("start_date", { ascending: true });

  if (error) throw error;
  if (!data || data.length === 0) return fallback; // table not synced yet — never show an empty screen

  return data.map((row) => ({
    id: row.id,
    name: row.name,
    tour: row.tour,
    location: row.location ?? "",
    country: row.country ?? "",
    startDate: row.start_date,
    endDate: row.end_date,
    break: row.break_name ?? row.location ?? "",
  }));
}

export function useCompetitions() {
  const { data, error, isLoading, isValidating, mutate } = useSWR<Competition[]>(
    "wsl-events",
    fetchFromSupabase,
    {
      refreshInterval: 5 * 60 * 1000, // re-check every 5 minutes
      fallbackData: fallback,          // show static data instantly on first load
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 60 * 1000,
    }
  );

  const sorted: CompetitionWithStatus[] = (data ?? fallback)
    .map((c) => ({ ...c, status: getStatus(c.startDate, c.endDate) }))
    .sort((a, b) => {
      const order = { live: 0, upcoming: 1, completed: 2 } as const;
      if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });

  return {
    competitions: sorted,
    isLoading,
    isValidating,
    isError: !!error,
    refresh: mutate,
  };
}
